import { useMemo, useState } from 'react'
import { Button, Switch, Table } from '@heroui/react'
import { useNavigate } from 'react-router-dom'

import { ModuleIntro } from '@/components/layout'
import { EmptyState, ErrorState, LoadingState, Pagination } from '@/components/ui/data-state'
import { FilterSelect } from '@/components/ui/filter-select'
import { LinkIcon, UnlinkIcon } from '@/components/ui/icons'
import { useBranches } from '@/features/business'
import { paths } from '@/routes/paths'

import {
  BRANCH_GRID_MAX,
  BRANCH_PAGE_SIZE,
  BranchView,
  BRANCH_SEARCH_DEBOUNCE_MS,
  isBranchKnown,
  isOnlyBranchOf,
  isSoldAt,
} from '../branch-links'
import { useBranchLinks, useDebouncedValue, useProducts } from '../hooks'
import { translated } from '../types'
import type { ProductListQuery, ProductSummary } from '../types'

import {
  BranchProductCell,
  BranchProductSearch,
  BranchRemovalConfirm,
  BranchWriteError,
} from './branch-product-parts'
import { BranchProductEditor } from './branch-product-editor'
import { BranchProductsGrid } from './branch-products-grid'

/** The one narrowing the "Show" picker offers. `null` there is every product. */
const SOLD_HERE = 'sold-here'

interface BranchProductsPanelProps {
  /** One product at a time, or every branch at once — see `BranchView`. */
  view: BranchView
  /**
   * The outlet the single-branch view is setting up, chosen in the module bar.
   *
   * Only read past `BRANCH_GRID_MAX` branches — below that the grid shows them
   * all and the bar offers no picker.
   */
  branchId: string | null
}

/**
 * Which products each branch sells — the Products module's Branches tab.
 *
 * Branch first, where the Products tab is product first: a merchant opening a
 * new outlet is choosing its menu, and doing that from the catalogue meant
 * opening every product's form to tick one box.
 *
 * Three ways in, chosen in the module bar. "By product" edits one product's
 * whole list and saves it — see `BranchProductEditor`. Otherwise a handful of
 * branches is one grid — see `BranchProductsGrid` — and a larger chain gets one
 * branch at a time, with a switch a row. Either way, turning a product
 * on adds that branch to the ones it already has and turning it off takes only
 * that one away; setting the whole list at once is still the product form's job.
 */
export function BranchProductsPanel({ view, branchId }: BranchProductsPanelProps) {
  const { branches, isLoading, error, retry } = useBranches()
  const navigate = useNavigate()

  if (isLoading) return <LoadingState />
  if (error) return <ErrorState subject="your branches" error={error} onRetry={retry} />

  if (branches.length === 0) {
    return (
      <EmptyState
        title="No branches yet."
        description="Products are sold at a branch. Add your first one under Business, then choose what it sells here."
        action={{ label: 'Go to Business', onPress: () => navigate(paths.business) }}
      />
    )
  }

  if (view === BranchView.Product) return <BranchProductEditor branches={branches} />
  if (branches.length <= BRANCH_GRID_MAX) return <BranchProductsGrid branches={branches} />

  const branch = branches.find((candidate) => candidate.id === branchId) ?? branches[0]

  // Keyed on the branch so its page, its search and any write still in flight
  // are dropped when the merchant moves to another — a pending switch belongs
  // to the outlet it was flipped for, not to the row that happens to be there.
  return (
    <SingleBranchProducts
      key={branch.id}
      branchId={branch.id}
      branchName={translated(branch.name, 'this branch')}
    />
  )
}

interface SingleBranchProductsProps {
  branchId: string
  branchName: string
}

function SingleBranchProducts({ branchId, branchName }: SingleBranchProductsProps) {
  const [search, setSearch] = useState('')
  const searchText = useDebouncedValue(search.trim(), BRANCH_SEARCH_DEBOUNCE_MS)
  const [show, setShow] = useState<string | null>(null)
  const [page, setPage] = useState(0)

  // A new search can strand the merchant on a page that no longer exists.
  // Reset during render rather than in an effect, so no frame shows it.
  const [lastSearchText, setLastSearchText] = useState(searchText)

  if (searchText !== lastSearchText) {
    setLastSearchText(searchText)
    setPage(0)
  }

  const isSoldHereOnly = show === SOLD_HERE

  const query = useMemo<ProductListQuery>(
    () => ({
      limit: BRANCH_PAGE_SIZE,
      offset: page * BRANCH_PAGE_SIZE,
      searchText: searchText || undefined,
      branchId: isSoldHereOnly ? branchId : undefined,
    }),
    [page, searchText, isSoldHereOnly, branchId],
  )

  const { products, total, isLoading, isRefreshing, error, retry } = useProducts(query)

  // The two totals the summary line reads, one row each since only the count is
  // wanted. Both sit under the `products` key, so a switch flipped below
  // refreshes them along with the list.
  const sold = useProducts({ limit: 1, offset: 0, branchId })
  const catalogue = useProducts({ limit: 1, offset: 0 })

  const links = useBranchLinks()

  const isIdle = (product: ProductSummary) => !links.isPending(product.id, branchId)
  const addable = products.filter(
    (product) => isBranchKnown(product) && !isSoldAt(product, branchId) && isIdle(product),
  )
  const removable = products.filter(
    (product) =>
      isSoldAt(product, branchId) && !isOnlyBranchOf(product, branchId) && isIdle(product),
  )

  const isFiltered = Boolean(searchText) || isSoldHereOnly
  const isCounted = !sold.isLoading && !catalogue.isLoading

  return (
    <>
      <ModuleIntro
        description={
          isCounted
            ? `${sold.total} of ${catalogue.total} products are sold at ${branchName}. Switching one on adds this branch and keeps its others.`
            : `Choose which products ${branchName} sells.`
        }
        action={
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="border-border h-9 rounded-full border px-4 text-xs"
              isDisabled={addable.length === 0}
              onPress={() => links.link(addable, branchId)}
            >
              <LinkIcon width={14} height={14} />
              {pageActionLabel('Add', addable.length)}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              className="border-border h-9 rounded-full border px-4 text-xs"
              isDisabled={removable.length === 0}
              onPress={() => links.unlink(removable, branchId, branchName)}
            >
              <UnlinkIcon width={14} height={14} />
              {pageActionLabel('Remove', removable.length)}
            </Button>
          </div>
        }
      />

      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <BranchProductSearch value={search} onChange={setSearch} />

        <FilterSelect
          label="Show"
          allLabel="All products"
          options={[{ value: SOLD_HERE, label: 'Sold here' }]}
          value={show}
          onChange={(next) => {
            setShow(next)
            setPage(0)
          }}
        />
      </div>

      <BranchWriteError error={links.error} />

      {isLoading ? (
        <LoadingState />
      ) : error ? (
        <ErrorState subject="your products" error={error} onRetry={retry} />
      ) : products.length === 0 ? (
        <EmptyState
          title={isFiltered ? 'Nothing matches.' : 'No products yet.'}
          description={
            isFiltered
              ? `Try a broader search, or show every product to add some to ${branchName}.`
              : 'Add products on the Products tab, then choose here which ones this branch sells.'
          }
          action={
            isFiltered
              ? {
                  label: 'Show all products',
                  onPress: () => {
                    setSearch('')
                    setShow(null)
                    setPage(0)
                  },
                }
              : undefined
          }
        />
      ) : (
        <>
          <div className={isRefreshing ? 'opacity-60 transition-opacity' : undefined}>
            <Table>
              <Table.ScrollContainer>
                <Table.Content aria-label={`Products sold at ${branchName}`}>
                  <Table.Header>
                    <Table.Column id="product" isRowHeader>
                      Product
                    </Table.Column>
                    <Table.Column id="elsewhere">Also sold at</Table.Column>
                    <Table.Column id="sold">Sold here</Table.Column>
                  </Table.Header>

                  <Table.Body>
                    {products.map((product) => {
                      const isLocked = isOnlyBranchOf(product, branchId)

                      return (
                        <Table.Row key={product.id} id={product.id}>
                          <Table.Cell>
                            <BranchProductCell product={product} />
                          </Table.Cell>

                          <Table.Cell>
                            <ElsewhereCell product={product} branchId={branchId} />
                          </Table.Cell>

                          <Table.Cell>
                            <span className="flex items-center gap-2">
                              <Switch
                                aria-label={`Sell ${translated(product.name)} at ${branchName}`}
                                isSelected={isSoldAt(product, branchId)}
                                isDisabled={!isBranchKnown(product) || isLocked || !isIdle(product)}
                                onChange={(isSelected) =>
                                  isSelected
                                    ? links.link([product], branchId)
                                    : links.unlink([product], branchId, branchName)
                                }
                              >
                                <Switch.Content>
                                  <Switch.Control>
                                    <Switch.Thumb />
                                  </Switch.Control>
                                </Switch.Content>
                              </Switch>

                              {/* Said rather than only shown greyed out: a switch
                                  that will not move with no reason given reads as
                                  broken. */}
                              {isLocked && (
                                <span className="text-muted text-xs">
                                  Only branch — add another first
                                </span>
                              )}
                            </span>
                          </Table.Cell>
                        </Table.Row>
                      )
                    })}
                  </Table.Body>
                </Table.Content>
              </Table.ScrollContainer>
            </Table>
          </div>

          <Pagination
            page={page}
            pageSize={BRANCH_PAGE_SIZE}
            total={total}
            onPageChange={setPage}
          />
        </>
      )}

      <BranchRemovalConfirm
        removal={links.removal}
        onConfirm={links.confirmRemoval}
        onCancel={links.cancelRemoval}
      />
    </>
  )
}

/** "Add 4 on this page", or "Add all on this page" once there is nothing left to add. */
function pageActionLabel(verb: string, count: number): string {
  return count > 0 ? `${verb} ${count} on this page` : `${verb} all on this page`
}

/**
 * The product's other outlets, so switching it off here can be judged against
 * where else it is still sold. A product sold nowhere at all is flagged.
 */
function ElsewhereCell({ product, branchId }: { product: ProductSummary; branchId: string }) {
  if (!product.branches) return <span className="text-muted text-xs">—</span>
  if (product.branches.length === 0) {
    return <span className="text-warning text-xs">Not sold anywhere</span>
  }

  const names = product.branches
    .filter((branch) => branch.id !== branchId)
    .map((branch) => translated(branch.name, 'Branch'))

  if (names.length === 0) return <span className="text-muted text-xs">—</span>

  return (
    <span className="text-muted text-xs" title={names.join(', ')}>
      {names.slice(0, 2).join(', ')}
      {names.length > 2 && ` +${names.length - 2}`}
    </span>
  )
}
